// src/components/SystemHubAnuncios.js

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import './SystemHubAnuncios.css';
import {
    FaPlus, FaPencilAlt, FaTrash, FaBullhorn, FaStar, FaClipboardList,
    FaChartLine, FaTimes, FaPaperPlane, FaGripLines, FaUsers, FaBuilding,
    FaCommentDots, FaCheckCircle
} from 'react-icons/fa';

// ============================================================================
// HELPERS
// ============================================================================
// [FASE 3 — modo cookie] A credencial de sessão agora é o cookie HttpOnly
// (blb_access), enviado automaticamente pelo axios (withCredentials). NÃO montar
// o header Authorization na mão: em modo cookie, localStorage.getItem('token') é
// null e isto geraria "Authorization: Bearer null" — que o backend rejeita com
// "jwt malformed" (401). Mantido como no-op para não alterar os 14 call sites;
// o interceptor do api.js cuida de cookie, x-tenant-id e X-CSRF-Token.
const authConfig = () => ({});

const PUBLICO_LABEL = {
    todos: 'Todos os Usuários',
    admins: 'Apenas Admins',
    operacional: 'Apenas Operacional'
};

const STATUS_LABEL = {
    ativo: 'Ativo', inativo: 'Inativo',
    ativa: 'Ativa', encerrada: 'Encerrada'
};

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : null);

// Classificação de cor do NPS (padrão de mercado)
const npsClasse = (score) => {
    if (score >= 75) return 'excelente';
    if (score >= 50) return 'otimo';
    if (score >= 0) return 'regular';
    return 'critico';
};

// ============================================================================
// MODAL BASE
// ============================================================================
const Modal = ({ title, icon, onClose, children, size = 'md' }) => (
    <div className="shcs-modal-overlay" onMouseDown={onClose}>
        <div
            className={`shcs-modal shcs-modal--${size}`}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <button className="shcs-modal__close" onClick={onClose} aria-label="Fechar">
                <FaTimes />
            </button>
            <h3 className="shcs-modal__title">{icon} {title}</h3>
            <div className="shcs-modal__body">{children}</div>
        </div>
    </div>
);

// ============================================================================
// ABA 1 — ANÚNCIOS GLOBAIS
// ============================================================================
const AnuncioModal = ({ anuncio, onClose, onSave }) => {
    const [form, setForm] = useState({
        titulo: anuncio?.titulo || '',
        mensagem: anuncio?.mensagem || '',
        status: anuncio?.status || 'ativo',
        publico_alvo: anuncio?.publico_alvo || 'todos',
        data_expiracao: anuncio?.data_expiracao?.split('T')[0] || ''
    });
    const [saving, setSaving] = useState(false);

    const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async () => {
        if (!form.titulo.trim() || !form.mensagem.trim()) {
            return toast.warn('Preencha título e mensagem.');
        }
        setSaving(true);
        await onSave({ ...anuncio, ...form, data_expiracao: form.data_expiracao || null });
        setSaving(false);
    };

    return (
        <Modal
            title={anuncio ? 'Editar Anúncio Global' : 'Novo Anúncio Global'}
            icon={<FaBullhorn />}
            onClose={onClose}
        >
            <div className="shcs-field">
                <label className="shcs-field__label">Título</label>
                <input className="shcs-input" name="titulo" value={form.titulo} onChange={change} />
            </div>
            <div className="shcs-field">
                <label className="shcs-field__label">Mensagem</label>
                <textarea className="shcs-textarea" name="mensagem" value={form.mensagem} onChange={change} rows="4" />
            </div>
            <div className="shcs-grid-2">
                <div className="shcs-field">
                    <label className="shcs-field__label">Status</label>
                    <select className="shcs-select" name="status" value={form.status} onChange={change}>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                    </select>
                </div>
                <div className="shcs-field">
                    <label className="shcs-field__label">Público-Alvo</label>
                    <select className="shcs-select" name="publico_alvo" value={form.publico_alvo} onChange={change}>
                        <option value="todos">Todos os Usuários</option>
                        <option value="admins">Apenas Admins</option>
                        <option value="operacional">Apenas Operacional</option>
                    </select>
                </div>
            </div>
            <div className="shcs-field">
                <label className="shcs-field__label">Data de Expiração (opcional)</label>
                <input className="shcs-input" type="date" name="data_expiracao" value={form.data_expiracao} onChange={change} />
                <small className="shcs-help">O anúncio deixará de ser exibido após esta data.</small>
            </div>
            <div className="shcs-modal__footer">
                <button className="shcs-btn shcs-btn--ghost" onClick={onClose}>Cancelar</button>
                <button className="shcs-btn shcs-btn--primary" onClick={submit} disabled={saving}>
                    {saving ? 'Salvando...' : (anuncio ? 'Salvar Alterações' : 'Publicar Anúncio')}
                </button>
            </div>
        </Modal>
    );
};

const AbaAnuncios = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [current, setCurrent] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/system-hub/announcements', authConfig());
            setItems(data);
        } catch {
            toast.error('Falha ao carregar anúncios globais.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (dados) => {
        try {
            if (dados.id) {
                await api.put(`/api/system-hub/announcements/${dados.id}`, dados, authConfig());
                toast.success('Anúncio atualizado com sucesso!');
            } else {
                await api.post('/api/system-hub/announcements', dados, authConfig());
                toast.success('Anúncio global criado com sucesso!');
            }
            setModal(false);
            load();
        } catch {
            toast.error('Erro ao salvar anúncio.');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este anúncio global?')) return;
        try {
            await api.delete(`/api/system-hub/announcements/${id}`, authConfig());
            toast.success('Anúncio excluído!');
            load();
        } catch {
            toast.error('Falha ao excluir o anúncio.');
        }
    };

    if (loading) return <div className="shcs-loading">Carregando anúncios...</div>;

    return (
        <>
            <div className="shcs-toolbar">
                <h2 className="shcs-toolbar__title">Anúncios Globais</h2>
                <button className="shcs-btn shcs-btn--success" onClick={() => { setCurrent(null); setModal(true); }}>
                    <FaPlus /> Novo Anúncio
                </button>
            </div>

            <div className="shcs-card">
                <table className="shcs-table">
                    <thead>
                        <tr>
                            <th className="col-titulo">Título</th>
                            <th className="col-status">Status</th>
                            <th className="col-publico">Público-Alvo</th>
                            <th className="col-data">Expira em</th>
                            <th className="col-acoes">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((a) => (
                            <tr key={a.id}>
                                <td className="col-titulo"><strong>{a.titulo}</strong></td>
                                <td className="col-status">
                                    <span className={`shcs-badge shcs-badge--${a.status}`}>
                                        {STATUS_LABEL[a.status] || a.status}
                                    </span>
                                </td>
                                <td className="col-publico">{PUBLICO_LABEL[a.publico_alvo] || a.publico_alvo}</td>
                                <td className="col-data">
                                    {fmtData(a.data_expiracao) || <span className="shcs-muted">Nunca</span>}
                                </td>
                                <td className="col-acoes">
                                    <div className="shcs-actions">
                                        <button className="shcs-icon-btn edit" title="Editar"
                                            onClick={() => { setCurrent(a); setModal(true); }}>
                                            <FaPencilAlt />
                                        </button>
                                        <button className="shcs-icon-btn delete" title="Excluir"
                                            onClick={() => remove(a.id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="shcs-empty">Nenhum anúncio global criado.</div>}
            </div>

            {modal && <AnuncioModal anuncio={current} onClose={() => setModal(false)} onSave={save} />}
        </>
    );
};

// ============================================================================
// ABA 2 — NPS
// ============================================================================
// Campos compartilhados de segmentação (tenant) + lembrete periódico.
const SegmentacaoFields = ({ clienteId, relembrar, onChange }) => {
    const [tenants, setTenants] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/api/system-hub/clientes', authConfig());
                setTenants(Array.isArray(data) ? data : []);
            } catch {
                // silencioso: sem a lista, cai para 'global' (todos os tenants).
            }
        })();
    }, []);

    return (
        <div className="shcs-grid-2">
            <div className="shcs-field">
                <label className="shcs-field__label">Segmentação</label>
                <select
                    className="shcs-select"
                    value={clienteId || ''}
                    onChange={(e) => onChange('cliente_id', e.target.value || null)}
                >
                    <option value="">Global — todos os tenants</option>
                    {tenants.map((t) => (
                        <option key={t.id} value={t.id}>{t.nome_empresa}</option>
                    ))}
                </select>
                <small className="shcs-help">Escolha uma empresa para direcionar, ou deixe global.</small>
            </div>
            <div className="shcs-field">
                <label className="shcs-field__label">Relembrar a cada (dias)</label>
                <input
                    className="shcs-input"
                    type="number"
                    min="1"
                    placeholder="Ex.: 3 (deixe vazio p/ não relembrar)"
                    value={relembrar || ''}
                    onChange={(e) => onChange('relembrar_a_cada_dias', e.target.value || null)}
                />
                <small className="shcs-help">Fica fixa no sino; reenvia lembrete nesse intervalo até responder.</small>
            </div>
        </div>
    );
};

const NpsModal = ({ campanha, onClose, onSave }) => {
    const editando = !!campanha;
    const [form, setForm] = useState({
        titulo: campanha?.titulo || '',
        pergunta: campanha?.pergunta || 'De 0 a 10, o quanto você recomenda o nosso sistema?',
        publico_alvo: campanha?.publico_alvo || 'todos',
        cliente_id: campanha?.cliente_id || null,
        status: campanha?.status || 'ativa',
        data_expiracao: campanha?.data_expiracao?.split('T')[0] || '',
        relembrar_a_cada_dias: campanha?.relembrar_a_cada_dias || null
    });
    const [saving, setSaving] = useState(false);
    const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async () => {
        if (!form.titulo.trim()) return toast.warn('Informe o título da campanha.');
        setSaving(true);
        await onSave({ id: campanha?.id, ...form, data_expiracao: form.data_expiracao || null });
        setSaving(false);
    };

    return (
        <Modal
            title={editando ? 'Editar Campanha NPS' : 'Nova Campanha NPS'}
            icon={<FaStar />}
            onClose={onClose}
        >
            <div className="shcs-field">
                <label className="shcs-field__label">Título da Campanha</label>
                <input className="shcs-input" name="titulo" value={form.titulo} onChange={change}
                    placeholder="Ex.: Pesquisa de Satisfação — Agosto/2026" />
            </div>
            <div className="shcs-field">
                <label className="shcs-field__label">Pergunta</label>
                <textarea className="shcs-textarea" name="pergunta" value={form.pergunta} onChange={change} rows="2" />
                <small className="shcs-help">Esta é a pergunta que o usuário verá junto da escala de 0 a 10.</small>
            </div>
            <div className="shcs-grid-2">
                <div className="shcs-field">
                    <label className="shcs-field__label">Público-Alvo</label>
                    <select className="shcs-select" name="publico_alvo" value={form.publico_alvo} onChange={change}>
                        <option value="todos">Todos os Usuários</option>
                        <option value="admins">Apenas Admins</option>
                        <option value="operacional">Apenas Operacional</option>
                    </select>
                </div>
                {editando && (
                    <div className="shcs-field">
                        <label className="shcs-field__label">Status</label>
                        <select className="shcs-select" name="status" value={form.status} onChange={change}>
                            <option value="ativa">Ativa</option>
                            <option value="inativa">Inativa</option>
                            <option value="encerrada">Encerrada</option>
                        </select>
                    </div>
                )}
                <div className="shcs-field">
                    <label className="shcs-field__label">Expira em (opcional)</label>
                    <input className="shcs-input" type="date" name="data_expiracao"
                        value={form.data_expiracao} onChange={change} />
                </div>
            </div>
            <SegmentacaoFields
                clienteId={form.cliente_id}
                relembrar={form.relembrar_a_cada_dias}
                onChange={(campo, valor) => setForm((f) => ({ ...f, [campo]: valor }))}
            />
            <div className="shcs-modal__footer">
                <button className="shcs-btn shcs-btn--ghost" onClick={onClose}>Cancelar</button>
                <button className="shcs-btn shcs-btn--primary" onClick={submit} disabled={saving}>
                    {saving ? 'Enviando...' : (editando
                        ? <>Salvar Alterações</>
                        : <><FaPaperPlane /> Disparar Campanha</>)}
                </button>
            </div>
        </Modal>
    );
};

const NpsResultadosModal = ({ campanha, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/api/system-hub/nps/campaigns/${campanha.id}/results`, authConfig());
                setData(res.data);
            } catch {
                toast.error('Falha ao carregar resultados.');
            } finally {
                setLoading(false);
            }
        })();
    }, [campanha.id]);

    const maxDist = data ? Math.max(1, ...data.distribuicao.map((d) => d.qtd)) : 1;

    return (
        <Modal title={`Resultados — ${campanha.titulo}`} icon={<FaChartLine />} onClose={onClose} size="lg">
            {loading && <div className="shcs-loading">Calculando NPS...</div>}
            {!loading && data && (
                <>
                    {data.resumo.total === 0 ? (
                        <div className="shcs-empty">Ainda não há respostas para esta campanha.</div>
                    ) : (
                        <>
                            <div className={`shcs-score-hero shcs-score-hero--${npsClasse(data.resumo.score)}`}>
                                <div className="shcs-score-hero__value">{data.resumo.score}</div>
                                <div className="shcs-score-hero__meta">
                                    <span className="shcs-score-hero__label">Net Promoter Score</span>
                                    <span className="shcs-score-hero__sub">
                                        {data.resumo.total} resposta(s) · média {data.resumo.media}
                                    </span>
                                </div>
                            </div>

                            <div className="shcs-segments">
                                <div className="shcs-segment shcs-segment--promotor">
                                    <strong>{data.resumo.promotores}</strong><span>Promotores (9–10)</span>
                                </div>
                                <div className="shcs-segment shcs-segment--neutro">
                                    <strong>{data.resumo.neutros}</strong><span>Neutros (7–8)</span>
                                </div>
                                <div className="shcs-segment shcs-segment--detrator">
                                    <strong>{data.resumo.detratores}</strong><span>Detratores (0–6)</span>
                                </div>
                            </div>

                            <h4 className="shcs-subtitle">Distribuição das notas</h4>
                            <div className="shcs-dist">
                                {Array.from({ length: 11 }, (_, n) => {
                                    const item = data.distribuicao.find((d) => Number(d.nota) === n);
                                    const qtd = item ? item.qtd : 0;
                                    const faixa = n <= 6 ? 'detrator' : n <= 8 ? 'neutro' : 'promotor';
                                    return (
                                        <div key={n} className="shcs-dist__col">
                                            <div className="shcs-dist__bar-wrap">
                                                <div
                                                    className={`shcs-dist__bar shcs-dist__bar--${faixa}`}
                                                    style={{ height: `${(qtd / maxDist) * 100}%` }}
                                                    title={`${qtd} resposta(s)`}
                                                />
                                            </div>
                                            <span className="shcs-dist__count">{qtd}</span>
                                            <span className="shcs-dist__label">{n}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            {data.porTenant.length > 0 && (
                                <>
                                    <h4 className="shcs-subtitle"><FaBuilding /> Por empresa</h4>
                                    <table className="shcs-mini-table">
                                        <thead>
                                            <tr><th>Empresa</th><th>Respostas</th><th>Promotores</th><th>Detratores</th></tr>
                                        </thead>
                                        <tbody>
                                            {data.porTenant.map((t, i) => (
                                                <tr key={i}>
                                                    <td>{t.nome_empresa}</td>
                                                    <td>{t.total}</td>
                                                    <td className="shcs-pos">{t.promotores}</td>
                                                    <td className="shcs-neg">{t.detratores}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {data.comentarios.length > 0 && (
                                <>
                                    <h4 className="shcs-subtitle"><FaCommentDots /> Comentários</h4>
                                    <div className="shcs-comments">
                                        {data.comentarios.map((c, i) => {
                                            const faixa = c.nota <= 6 ? 'detrator' : c.nota <= 8 ? 'neutro' : 'promotor';
                                            return (
                                                <div key={i} className={`shcs-comment shcs-comment--${faixa}`}>
                                                    <div className="shcs-comment__head">
                                                        <span className={`shcs-nota-pill shcs-nota-pill--${faixa}`}>{c.nota}</span>
                                                        <strong>{c.usuario_nome || 'Usuário'}</strong>
                                                        {c.nome_empresa && <span className="shcs-comment__empresa">· {c.nome_empresa}</span>}
                                                    </div>
                                                    <p className="shcs-comment__text">{c.comentario}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Modal>
    );
};

const AbaNps = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [current, setCurrent] = useState(null);
    const [resultado, setResultado] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/system-hub/nps/campaigns', authConfig());
            setItems(data);
        } catch {
            toast.error('Falha ao carregar campanhas de NPS.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (dados) => {
        try {
            if (dados.id) {
                await api.put(`/api/system-hub/nps/campaigns/${dados.id}`, dados, authConfig());
                toast.success('Campanha atualizada!');
            } else {
                const { data } = await api.post('/api/system-hub/nps/campaigns', dados, authConfig());
                toast.success(`Campanha disparada para ${data.notificados} usuário(s)!`);
            }
            setModal(false);
            load();
        } catch {
            toast.error('Erro ao salvar campanha.');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Excluir esta campanha e todas as suas respostas?')) return;
        try {
            await api.delete(`/api/system-hub/nps/campaigns/${id}`, authConfig());
            toast.success('Campanha excluída!');
            load();
        } catch {
            toast.error('Falha ao excluir campanha.');
        }
    };

    if (loading) return <div className="shcs-loading">Carregando campanhas...</div>;

    return (
        <>
            <div className="shcs-toolbar">
                <h2 className="shcs-toolbar__title">Campanhas de NPS</h2>
                <button className="shcs-btn shcs-btn--success" onClick={() => { setCurrent(null); setModal(true); }}>
                    <FaPlus /> Nova Campanha
                </button>
            </div>

            {items.length === 0 ? (
                <div className="shcs-card"><div className="shcs-empty">Nenhuma campanha de NPS criada.</div></div>
            ) : (
                <div className="shcs-nps-grid">
                    {items.map((c) => {
                        const total = Number(c.total_respostas || 0);
                        const prom = Number(c.promotores || 0);
                        const det = Number(c.detratores || 0);
                        const score = total > 0 ? Math.round(((prom - det) / total) * 100) : null;
                        return (
                            <div key={c.id} className="shcs-nps-card">
                                <div className="shcs-nps-card__top">
                                    <span className={`shcs-badge shcs-badge--${c.status}`}>
                                        {STATUS_LABEL[c.status] || c.status}
                                    </span>
                                    <div className="shcs-actions">
                                        <button className="shcs-icon-btn edit" title="Editar"
                                            onClick={() => { setCurrent(c); setModal(true); }}>
                                            <FaPencilAlt />
                                        </button>
                                        <button className="shcs-icon-btn delete" title="Excluir"
                                            onClick={() => remove(c.id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                                <h3 className="shcs-nps-card__title">{c.titulo}</h3>
                                <p className="shcs-nps-card__pergunta">{c.pergunta}</p>
                                <div className="shcs-nps-card__foot">
                                    <div className={`shcs-nps-card__score shcs-nps-card__score--${score === null ? 'vazio' : npsClasse(score)}`}>
                                        {score === null ? '—' : score}
                                        <small>NPS</small>
                                    </div>
                                    <div className="shcs-nps-card__stats">
                                        <span><FaUsers /> {total} resposta(s)</span>
                                        <button className="shcs-link-btn" onClick={() => setResultado(c)}>
                                            <FaChartLine /> Ver resultados
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {modal && <NpsModal campanha={current} onClose={() => setModal(false)} onSave={save} />}
            {resultado && <NpsResultadosModal campanha={resultado} onClose={() => setResultado(null)} />}
        </>
    );
};

// ============================================================================
// ABA 3 — PESQUISAS DE USO
// ============================================================================
const tipoPerguntaLabel = {
    texto: 'Texto livre', nota: 'Nota (0–10)', multipla: 'Múltipla escolha', sim_nao: 'Sim / Não'
};

const PesquisaModal = ({ onClose, onSave }) => {
    const [meta, setMeta] = useState({ titulo: '', descricao: '', publico_alvo: 'todos', cliente_id: null, data_expiracao: '', relembrar_a_cada_dias: null });
    const [perguntas, setPerguntas] = useState([{ texto: '', tipo: 'texto', opcoes: '', obrigatoria: true }]);
    const [saving, setSaving] = useState(false);

    const changeMeta = (e) => setMeta({ ...meta, [e.target.name]: e.target.value });

    const changePergunta = (i, campo, valor) => {
        setPerguntas(perguntas.map((p, idx) => (idx === i ? { ...p, [campo]: valor } : p)));
    };
    const addPergunta = () =>
        setPerguntas([...perguntas, { texto: '', tipo: 'texto', opcoes: '', obrigatoria: true }]);
    const removePergunta = (i) =>
        setPerguntas(perguntas.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!meta.titulo.trim()) return toast.warn('Informe o título da pesquisa.');
        const validas = perguntas.filter((p) => p.texto.trim());
        if (validas.length === 0) return toast.warn('Adicione ao menos uma pergunta.');

        const payload = {
            ...meta,
            data_expiracao: meta.data_expiracao || null,
            perguntas: validas.map((p) => ({
                texto: p.texto.trim(),
                tipo: p.tipo,
                obrigatoria: p.obrigatoria,
                opcoes: p.tipo === 'multipla'
                    ? p.opcoes.split(',').map((o) => o.trim()).filter(Boolean)
                    : null
            }))
        };
        setSaving(true);
        await onSave(payload);
        setSaving(false);
    };

    return (
        <Modal title="Nova Pesquisa de Uso" icon={<FaClipboardList />} onClose={onClose} size="lg">
            <div className="shcs-field">
                <label className="shcs-field__label">Título</label>
                <input className="shcs-input" name="titulo" value={meta.titulo} onChange={changeMeta}
                    placeholder="Ex.: Como você usa o módulo Financeiro?" />
            </div>
            <div className="shcs-field">
                <label className="shcs-field__label">Descrição (opcional)</label>
                <textarea className="shcs-textarea" name="descricao" value={meta.descricao} onChange={changeMeta} rows="2" />
            </div>
            <div className="shcs-grid-2">
                <div className="shcs-field">
                    <label className="shcs-field__label">Público-Alvo</label>
                    <select className="shcs-select" name="publico_alvo" value={meta.publico_alvo} onChange={changeMeta}>
                        <option value="todos">Todos os Usuários</option>
                        <option value="admins">Apenas Admins</option>
                        <option value="operacional">Apenas Operacional</option>
                    </select>
                </div>
                <div className="shcs-field">
                    <label className="shcs-field__label">Expira em (opcional)</label>
                    <input className="shcs-input" type="date" name="data_expiracao"
                        value={meta.data_expiracao} onChange={changeMeta} />
                </div>
            </div>
            <SegmentacaoFields
                clienteId={meta.cliente_id}
                relembrar={meta.relembrar_a_cada_dias}
                onChange={(campo, valor) => setMeta((m) => ({ ...m, [campo]: valor }))}
            />

            <div className="shcs-builder">
                <div className="shcs-builder__head">
                    <h4 className="shcs-subtitle"><FaGripLines /> Perguntas</h4>
                    <button className="shcs-btn shcs-btn--ghost shcs-btn--sm" onClick={addPergunta}>
                        <FaPlus /> Adicionar pergunta
                    </button>
                </div>

                {perguntas.map((p, i) => (
                    <div key={i} className="shcs-question">
                        <div className="shcs-question__num">{i + 1}</div>
                        <div className="shcs-question__body">
                            <input
                                className="shcs-input"
                                placeholder="Digite a pergunta..."
                                value={p.texto}
                                onChange={(e) => changePergunta(i, 'texto', e.target.value)}
                            />
                            <div className="shcs-question__row">
                                <select className="shcs-select shcs-select--sm" value={p.tipo}
                                    onChange={(e) => changePergunta(i, 'tipo', e.target.value)}>
                                    {Object.entries(tipoPerguntaLabel).map(([v, l]) => (
                                        <option key={v} value={v}>{l}</option>
                                    ))}
                                </select>
                                <label className="shcs-check">
                                    <input type="checkbox" checked={p.obrigatoria}
                                        onChange={(e) => changePergunta(i, 'obrigatoria', e.target.checked)} />
                                    Obrigatória
                                </label>
                                {perguntas.length > 1 && (
                                    <button className="shcs-icon-btn delete shcs-icon-btn--sm" title="Remover"
                                        onClick={() => removePergunta(i)}>
                                        <FaTrash />
                                    </button>
                                )}
                            </div>
                            {p.tipo === 'multipla' && (
                                <input
                                    className="shcs-input shcs-input--sm"
                                    placeholder="Opções separadas por vírgula (ex.: Sim, Não, Talvez)"
                                    value={p.opcoes}
                                    onChange={(e) => changePergunta(i, 'opcoes', e.target.value)}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="shcs-modal__footer">
                <button className="shcs-btn shcs-btn--ghost" onClick={onClose}>Cancelar</button>
                <button className="shcs-btn shcs-btn--primary" onClick={submit} disabled={saving}>
                    {saving ? 'Enviando...' : <><FaPaperPlane /> Disparar Pesquisa</>}
                </button>
            </div>
        </Modal>
    );
};

const PesquisaResultadosModal = ({ pesquisa, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/api/system-hub/surveys/${pesquisa.id}/results`, authConfig());
                setData(res.data);
            } catch {
                toast.error('Falha ao carregar resultados.');
            } finally {
                setLoading(false);
            }
        })();
    }, [pesquisa.id]);

    return (
        <Modal title={`Resultados — ${pesquisa.titulo}`} icon={<FaChartLine />} onClose={onClose} size="lg">
            {loading && <div className="shcs-loading">Carregando respostas...</div>}
            {!loading && data && (
                <>
                    <div className="shcs-resp-summary">
                        <FaCheckCircle /> {data.total_respondentes} respondente(s)
                    </div>
                    {data.resultados.length === 0 && (
                        <div className="shcs-empty">Esta pesquisa não possui perguntas.</div>
                    )}
                    {data.resultados.map((q) => (
                        <div key={q.pergunta_id} className="shcs-result-block">
                            <div className="shcs-result-block__head">
                                <strong>{q.texto}</strong>
                                <span className="shcs-tag">{tipoPerguntaLabel[q.tipo] || q.tipo}</span>
                            </div>

                            {q.tipo === 'nota' && q.agregado && (
                                <div className="shcs-result-metric">
                                    Média: <strong>{q.agregado.media}</strong> · {q.agregado.total} nota(s)
                                </div>
                            )}

                            {(q.tipo === 'multipla' || q.tipo === 'sim_nao') && q.agregado && (
                                <div className="shcs-result-bars">
                                    {Object.entries(q.agregado).map(([opcao, qtd]) => {
                                        const pct = q.total_respostas ? Math.round((qtd / q.total_respostas) * 100) : 0;
                                        return (
                                            <div key={opcao} className="shcs-result-bar">
                                                <div className="shcs-result-bar__label">{opcao}</div>
                                                <div className="shcs-result-bar__track">
                                                    <div className="shcs-result-bar__fill" style={{ width: `${pct}%` }} />
                                                </div>
                                                <div className="shcs-result-bar__val">{qtd} ({pct}%)</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {q.tipo === 'texto' && (
                                <div className="shcs-comments">
                                    {q.respostas.length === 0 && <span className="shcs-muted">Sem respostas.</span>}
                                    {q.respostas.map((r, i) => (
                                        <div key={i} className="shcs-comment">
                                            <div className="shcs-comment__head">
                                                <strong>{r.usuario_nome || 'Usuário'}</strong>
                                                {r.nome_empresa && <span className="shcs-comment__empresa">· {r.nome_empresa}</span>}
                                            </div>
                                            <p className="shcs-comment__text">{r.resposta_valor}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </>
            )}
        </Modal>
    );
};

const AbaPesquisas = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [resultado, setResultado] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/system-hub/surveys', authConfig());
            setItems(data);
        } catch {
            toast.error('Falha ao carregar pesquisas.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (payload) => {
        try {
            const { data } = await api.post('/api/system-hub/surveys', payload, authConfig());
            toast.success(`Pesquisa disparada para ${data.notificados} usuário(s)!`);
            setModal(false);
            load();
        } catch {
            toast.error('Erro ao criar pesquisa.');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Excluir esta pesquisa e todas as respostas?')) return;
        try {
            await api.delete(`/api/system-hub/surveys/${id}`, authConfig());
            toast.success('Pesquisa excluída!');
            load();
        } catch {
            toast.error('Falha ao excluir pesquisa.');
        }
    };

    if (loading) return <div className="shcs-loading">Carregando pesquisas...</div>;

    return (
        <>
            <div className="shcs-toolbar">
                <h2 className="shcs-toolbar__title">Pesquisas de Uso</h2>
                <button className="shcs-btn shcs-btn--success" onClick={() => setModal(true)}>
                    <FaPlus /> Nova Pesquisa
                </button>
            </div>

            <div className="shcs-card">
                <table className="shcs-table">
                    <thead>
                        <tr>
                            <th className="col-titulo">Título</th>
                            <th className="col-status">Status</th>
                            <th className="col-publico">Público-Alvo</th>
                            <th className="col-num">Perguntas</th>
                            <th className="col-num">Respondentes</th>
                            <th className="col-acoes">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id}>
                                <td className="col-titulo"><strong>{p.titulo}</strong></td>
                                <td className="col-status">
                                    <span className={`shcs-badge shcs-badge--${p.status}`}>
                                        {STATUS_LABEL[p.status] || p.status}
                                    </span>
                                </td>
                                <td className="col-publico">{PUBLICO_LABEL[p.publico_alvo] || p.publico_alvo}</td>
                                <td className="col-num">{p.total_perguntas}</td>
                                <td className="col-num">{p.total_respondentes}</td>
                                <td className="col-acoes">
                                    <div className="shcs-actions">
                                        <button className="shcs-icon-btn view" title="Ver resultados"
                                            onClick={() => setResultado(p)}>
                                            <FaChartLine />
                                        </button>
                                        <button className="shcs-icon-btn delete" title="Excluir"
                                            onClick={() => remove(p.id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="shcs-empty">Nenhuma pesquisa de uso criada.</div>}
            </div>

            {modal && <PesquisaModal onClose={() => setModal(false)} onSave={save} />}
            {resultado && <PesquisaResultadosModal pesquisa={resultado} onClose={() => setResultado(null)} />}
        </>
    );
};

// ============================================================================
// COMPONENTE PRINCIPAL — CENTRAL COM ABAS
// ============================================================================
const TABS = [
    { id: 'anuncios', label: 'Anúncios Globais', icon: <FaBullhorn /> },
    { id: 'nps', label: 'NPS', icon: <FaStar /> },
    { id: 'pesquisas', label: 'Pesquisas de Uso', icon: <FaClipboardList /> }
];

export default function SystemHubAnuncios() {
    const [aba, setAba] = useState('anuncios');

    return (
        <div className="shcs-wrapper">
            <div className="shcs-header">
                <h1 className="shcs-header__title"><FaBullhorn /> Central de Comunicação &amp; CS</h1>
            </div>

            <div className="shcs-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        className={`shcs-tab ${aba === t.id ? 'shcs-tab--active' : ''}`}
                        onClick={() => setAba(t.id)}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            <div className="shcs-panel">
                {aba === 'anuncios' && <AbaAnuncios />}
                {aba === 'nps' && <AbaNps />}
                {aba === 'pesquisas' && <AbaPesquisas />}
            </div>
        </div>
    );
}